import {
  Sector,
  SectorFilterInputObject,
  SectorsQueryVariables,
  TaskFilterInputObject,
  useSectorsQuery,
} from 'graphql-common';
import _get from 'lodash/get';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters from '@lib/utils/createSearchFilters';
import { ListQueryHookProps, ListQueryHookResult } from '../types';

export default function useSectorsQueryHook({
  additionalFilters,
  paginationParams,
  searchQuery,
  usePrevious = true,
  skip,
  fetchPolicy = 'cache-and-network',
  includeFullData = true,
  includePerformanceDataByTaskDeadlinesMissed = false,
  includePerformanceDataByTaskResults = false,
  taskFilters,
}: ListQueryHookProps & {
  includeFullData?: boolean;
  includePerformanceDataByTaskDeadlinesMissed?: boolean;
  includePerformanceDataByTaskResults?: boolean;
  taskFilters?: TaskFilterInputObject[];
}): ListQueryHookResult<Sector> {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    paginationParams,
    GetUrlParams.OrderingDirection,
  );
  const sectorsQueryVariables: SectorsQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
    includeFullData,
    includePerformanceDataByTaskDeadlinesMissed,
    includePerformanceDataByTaskResults,
  };
  const filters: SectorFilterInputObject[] = [];
  if (searchQuery) {
    filters.push(
      ...createSearchFilters(searchQuery, [
        { fieldName: 'name' },
        { fieldName: 'code' },
      ]),
    );
  }
  if (additionalFilters?.length) {
    filters.push(...(additionalFilters as SectorFilterInputObject[]));
  }
  if (filters.length) {
    sectorsQueryVariables.filters = filters;
  }
  if (taskFilters?.length) {
    sectorsQueryVariables.taskFilters = taskFilters;
  }
  if (orderingField && orderingDirection) {
    sectorsQueryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const sectorsQuery = useSectorsQuery({
    fetchPolicy,
    variables: sectorsQueryVariables,
    skip,
  });
  const { loading, data, previousData, refetch, error } = sectorsQuery;
  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const collection =
    !usePrevious && loading
      ? []
      : (_get(queryData, 'data.collection', []) as Sector[]);
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);
  return {
    refetch,
    error,
    firstLoading,
    collection,
    totalCount,
    totalPages,
    loading,
  };
}
